import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const INVOICE_STATUSES = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
];

function InvoiceForm({ open, onClose, invoiceId, companyId, onSaved, onSaveAndAddAnother }) {
    const { register, handleSubmit, formState: { errors }, reset, control } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingInvoice, setLoadingInvoice] = useState(false);

    // Load employees for the company
    const { data: employeesData } = useQuery(
        ['employees', companyId],
        () => {
            if (!companyId) return { results: [], count: 0 };
            return JobHttpService.loadEmployees({ company: companyId });
        },
        {
            enabled: !!companyId,
            keepPreviousData: true
        }
    );

    // Load jobs for the company
    const { data: jobsData } = useQuery(
        ['employerJobs', companyId],
        () => {
            if (!companyId) return { results: [], count: 0 };
            return JobHttpService.loadJobs({ filters: { company: companyId } });
        },
        {
            enabled: !!companyId,
            keepPreviousData: true
        }
    );

    const employees = employeesData?.results || employeesData || [];
    const jobs = jobsData?.results || [];

    useEffect(() => {
        if (open) {
            if (invoiceId) {
                loadInvoice();
            } else {
                reset({
                    employee: '',
                    job: '',
                    issue_date: null,
                    due_date: null,
                    total_amount: '',
                    status: 'pending'
                });
                setError('');
            }
        }
    }, [open, invoiceId, reset]);

    const loadInvoice = async () => {
        if (!invoiceId) return;
        
        setLoadingInvoice(true);
        setError('');
        try {
            const invoice = await JobHttpService.loadInvoice(invoiceId);
            reset({
                employee: invoice.employee?.id || '',
                job: invoice.job?.id || '',
                issue_date: invoice.issue_date ? dayjs(invoice.issue_date) : null,
                due_date: invoice.due_date ? dayjs(invoice.due_date) : null,
                total_amount: invoice.total_amount || '',
                status: invoice.status || 'pending'
            });
        } catch (err) {
            console.error('Error loading invoice:', err);
            setError(err?.message || 'Failed to load invoice');
        } finally {
            setLoadingInvoice(false);
        }
    };

    const onSubmit = async (data, saveAndAddAnother = false) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                employee: data.employee,
                company: companyId,
                job: data.job || null,
                issue_date: data.issue_date ? dayjs(data.issue_date).format('YYYY-MM-DD') : null,
                due_date: data.due_date ? dayjs(data.due_date).format('YYYY-MM-DD') : null,
                total_amount: data.total_amount ? parseFloat(data.total_amount) : null,
                status: data.status || 'pending'
            };

            let response;
            if (invoiceId) {
                response = await JobHttpService.updateInvoice(invoiceId, submitData);
            } else {
                response = await JobHttpService.createInvoice(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                
                if (saveAndAddAnother) {
                    reset({
                        employee: '',
                        job: '',
                        issue_date: null,
                        due_date: null,
                        total_amount: '',
                        status: 'pending'
                    });
                    setError('');
                    if (onSaveAndAddAnother) {
                        onSaveAndAddAnother();
                    }
                } else {
                    onClose();
                }
            } else {
                setError(response?.detail || response?.error || 'Failed to save invoice');
            }
        } catch (err) {
            console.error('Error saving invoice:', err);
            setError(err?.message || 'Failed to save invoice. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <form onSubmit={handleSubmit((data) => onSubmit(data, false))}>
                <DialogTitle>
                    {invoiceId ? 'Edit Invoice' : 'Create New Invoice'}
                </DialogTitle>
                <DialogContent>
                    {loadingInvoice && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingInvoice && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Employee</InputLabel>
                                        <Select
                                            label="Employee"
                                            {...register("employee", {
                                                required: "Employee is required"
                                            })}
                                            error={Boolean(errors.employee)}
                                        >
                                            {employees.map((employee) => (
                                                <MenuItem key={employee.id} value={employee.id}>
                                                    {employee.first_name} {employee.last_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {errors.employee && (
                                        <Alert severity="error" sx={{ mt: 0.5 }}>
                                            {errors.employee.message}
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Job (Optional)</InputLabel>
                                        <Select
                                            label="Job (Optional)"
                                            {...register("job")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {jobs.map((job) => (
                                                <MenuItem key={job.id} value={job.id}>
                                                    {job.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="issue_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Issue Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="due_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Due Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Amount"
                                        type="number"
                                        step="0.01"
                                        {...register("total_amount")}
                                        error={Boolean(errors.total_amount)}
                                        helperText={errors.total_amount?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            label="Status"
                                            {...register("status")}
                                        >
                                            {INVOICE_STATUSES.map((status) => (
                                                <MenuItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || loadingInvoice}
                    >
                        {loading ? <CircularProgress size={20} /> : (invoiceId ? 'Update' : 'Save')}
                    </Button>
                    {!invoiceId && (
                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={loading || loadingInvoice}
                            onClick={handleSubmit((data) => onSubmit(data, true))}
                        >
                            Save and add another
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        fullWidth
                        color="inherit"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default InvoiceForm;
